import { useState } from "react";
import { Pencil, Trash2, ImageOff } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { TableRow, TableCell } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { ActionButton } from "@/components/ui/action-button";
import { ImagePreview } from "@/components/ui/image-preview";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CategoryChip } from "@/components/ui/category-chip";
import { StatusBadge } from "./StatusBadge";
import { EventDialog } from "./EventDialog";
import { useUpdateEventStatus, useDeleteEvent } from "@/hooks/useEvents";
import { toast } from "sonner";
import type { EventResponse, Category, EventStatus } from "@/types";

function formatDate(dateStr: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(dateStr));
}

interface EventRowProps {
  event: EventResponse;
  categories: Category[];
  selected: boolean;
  onToggle: () => void;
}

export function EventRow({ event, categories, selected, onToggle }: EventRowProps) {
  const updateStatus = useUpdateEventStatus();
  const deleteEvent = useDeleteEvent();
  const [editOpen, setEditOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const category = categories.find((c) => c.id === event.categoryId);

  const handleStatusChange = async (status: string) => {
    if (status === "rejected") {
      setRejectionReason("");
      setRejectOpen(true);
      return;
    }
    try {
      await updateStatus.mutateAsync({ id: event.id, status: status as EventStatus });
      toast.success("Status zaktualizowany");
    } catch (err) {
      toast.error("Nie udało się zmienić statusu", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleReject = async () => {
    try {
      await updateStatus.mutateAsync({
        id: event.id,
        status: "rejected",
        rejectionReason: rejectionReason || undefined,
      });
      toast.success("Wydarzenie odrzucone");
      setRejectOpen(false);
    } catch (err) {
      toast.error("Nie udało się odrzucić wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEvent.mutateAsync(event.id);
      toast.success("Wydarzenie usunięte");
    } catch (err) {
      toast.error("Nie udało się usunąć wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Checkbox checked={selected} onCheckedChange={onToggle} />
        </TableCell>
        <TableCell className="w-[70px] min-w-[70px]">
          {event.coverImage ? (
            <ImagePreview src={event.coverImage.thumb} alt={event.title} />
          ) : (
            <div className="w-[70px] h-[70px] bg-slate-100 rounded flex items-center justify-center">
              <ImageOff className="h-5 w-5 text-slate-400" />
            </div>
          )}
        </TableCell>
        <TableCell className="max-w-xs">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate">{event.title}</p>
            {event.isPreview && (
              <span className="inline-flex shrink-0 items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                Podgląd
              </span>
            )}
          </div>
          {event.parentTitle && (
            <p className="text-xs text-blue-600 truncate">Część: {event.parentTitle}</p>
          )}
          <p className="text-xs text-muted-foreground truncate">{event.id}</p>
        </TableCell>
        <TableCell className="whitespace-nowrap">{formatDate(event.date)}</TableCell>
        <TableCell className="max-w-32 truncate">{event.locationName}</TableCell>
        <TableCell>
          <Select
            value={event.status}
            onValueChange={handleStatusChange}
            disabled={updateStatus.isPending}
          >
            <SelectTrigger className="h-8 w-auto gap-1.5 border-0 bg-transparent px-1.5 shadow-none hover:bg-black/5 focus:ring-0 focus:ring-offset-0 [&>span]:line-clamp-none">
              <SelectValue>
                <StatusBadge status={event.status} rejectionReason={event.rejectionReason} />
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Oczekujące</SelectItem>
              <SelectItem value="approved">Zatwierdzone</SelectItem>
              <SelectItem value="rejected">Odrzucone</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <CategoryChip category={category} />
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <ActionButton
              tooltip="Edytuj"
              className="h-8 w-8"
              onClick={() => setEditOpen(true)}
              disabled={deleteEvent.isPending}
            >
              <Pencil className="h-3.5 w-3.5" />
            </ActionButton>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <ActionButton
                  tooltip="Usuń"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  disabled={deleteEvent.isPending}
                >
                  {deleteEvent.isPending ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </ActionButton>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Usuń wydarzenie</AlertDialogTitle>
                  <AlertDialogDescription>
                    Czy na pewno chcesz usunąć wydarzenie „{event.title}"? Tej operacji nie można
                    cofnąć.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Anuluj</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Usuń
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      </TableRow>
      <EventDialog event={event} open={editOpen} onOpenChange={setEditOpen} />
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Odrzuć wydarzenie</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Powód odrzucenia (opcjonalny)</Label>
            <Input
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="np. Brak wymaganych informacji..."
            />
            <p className="text-xs text-muted-foreground">
              Widoczny dla organizatora po najechaniu na status.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Anuluj
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={updateStatus.isPending}>
              {updateStatus.isPending ? "Odrzucanie..." : "Odrzuć"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
